import { eventBus } from "../observability/eventBus";
import { makeEvent } from "../observability/eventSchema";
import { useChatStore } from "../state/chatStore";

// Silence escalation (continuous conversation): user 発話を N 秒待っても VAD が start を検出しない時に
// escalation callback を発火する。初期値 10s / 20s / 30s の 3 段。
// (spec: docs/superpowers/specs/2026-07-07-m3-continuous-conversation-requirements.md §3.5.1)
// N の値は §4.3 Q7 でチューニング。
type Opts = {
  escalations: number[]; // 各段階の待ち時間 (ms)。default [10000, 10000, 10000]
  onEscalation: (level: number) => void;
};

export class SilenceTimer {
  private handle: ReturnType<typeof setTimeout> | null = null;
  private level = 0;

  constructor(private opts: Opts) {}

  start(): void {
    // Bug fix (2026-07-09): 既存 timer を clear せずに schedule すると、
    // 複数の setTimeout が並行に走って onEscalation が重複発火する
    // (log: 「少し休憩しましょうか」× 5 回連発の原因)。
    this.clear();
    this.level = 0;
    this.schedule();
  }

  /**
   * 発話が始まったときに呼ぶ。**level を 0 に戻して計測を止める** (張り直さない)。
   *
   * **ここで schedule() してはいけない** (2026-08-05 の社内パイロットで実測):
   * reset() は `onSpeechStart` から呼ばれるので、schedule すると**話し始めた瞬間に
   * 10 秒のカウントダウンが始まり、10 秒を超えて話し続けると 100% 割り込む**。
   * 本番ログでは `silence.level_up` 22 件中 16 件がユーザーの発話中で、
   * **16 件すべてが `vad.speech_start` のちょうど 10.00 秒後**だった。
   * 割り込みの読み上げ中に発話が終わると `onSpeechEnd` の `isPlaying()` ガードに
   * 落ちるため、**その回答は丸ごと破棄される** (本編の発話の 21.8% が消えた原因の
   * 14/22 件)。`long` 設問では発話の 17.8% が 10 秒を超えるので必ず再発する。
   *
   * 張り直しは**発話が終わってから**呼び出し側が行う — `onSpeechEnd` と
   * `onVADMisfire` の両方が `armSilence()` を通す。**misfire 側を配線しないと、
   * 200ms 未満で終わった物音の後に計測が二度と始まらない** (level 1 の
   * テキスト退避 = 原則 5 の出口が永久に到達不能になる)。
   *
   * 計測: docs/measurements/2026-08-05-internal-pilot-session-logs.md §2.3
   */
  reset(): void {
    // spec: docs/superpowers/specs/2026-07-11-r2-log-expansion-design.md §6.2
    // event #17 silence.recovery — user が escalation 状態から
    // 回復した瞬間を検知する (level > 0 のときのみ、通常 start は無視)。
    // **この emit は stop() には無い**ので、ここを単に stop() へ置き換えると
    // 「回復した」という観測点が消える (パイロットでは 12 件記録されている)。
    if (this.level > 0) {
      const sessionId = useChatStore.getState().sessionId;
      if (sessionId) {
        eventBus.emit(
          makeEvent("silence.recovery", sessionId, {
            fromLevel: this.level,
          })
        );
      }
    }
    this.clear();
    this.level = 0;
  }

  stop(): void {
    this.clear();
  }

  /**
   * stop() で中断した待機を **level を保持したまま** 再開する。
   *
   * AI の読み上げ中は無音カウントを進めてはいけない (user は黙って聞いているだけ) ので
   * ChatScreen.playTts が前後で stop()/resume() を呼ぶ。ここで start() を使うと level が
   * 0 に戻り、「聞こえていますでしょうか」(level 0) だけが無限に繰り返されて level 1 の
   * 「マイクが届いていないかもしれません + テキスト入力を強制 expand」に永久に到達しない
   * (2026-07-25 実機で発生。voice-first 原則 5 の fallback が機能しなくなる)。
   *
   * clear() してから schedule() するので、多重呼び出しでも timer は 1 本だけになる。
   * 全 level を撃ち終えた後は schedule() が早期 return するため何も起きない。
   */
  resume(): void {
    this.clear();
    this.schedule();
  }

  private schedule(): void {
    if (this.level >= this.opts.escalations.length) return;
    const delay = this.opts.escalations[this.level];
    // this.level は setTimeout callback 内で +1 されるため、closure に snapshot する
    // (emit の payload と opts.onEscalation の引数を一致させる)。
    const level = this.level;
    this.handle = setTimeout(() => {
      // spec §6.2 event #16 silence.level_up — level は 0|1|2 の 3 段。
      const sessionId = useChatStore.getState().sessionId;
      if (sessionId) {
        eventBus.emit(
          makeEvent("silence.level_up", sessionId, {
            level,
            delayMs: delay,
          })
        );
      }
      this.opts.onEscalation(level);
      this.level += 1;
      this.schedule();
    }, delay);
  }

  private clear(): void {
    if (this.handle) {
      clearTimeout(this.handle);
      this.handle = null;
    }
  }
}
